import { z } from "zod";
import { ApiDefaultSchema } from "~/api/types";
import type { ScreenContent } from "~/app/hooks/analytics/tracker";
import type { TStoreNavAnchorNames } from "../components/StoreNav";

export const StoreSchemaDiamondDetails = z.object({
  Tier: z.string().nullish(),
  Icon: z.string().nullish(),
  IconUpload: z.string().nullish(),
  IsSaleBadgeEnabled: z.boolean(),
  Storev2ItemPromoStickerType: z.string().nullish(),
  Subtitle: z.string().nullish(),
  Title: z.string().nullish(),
  SaleBadgeText: z.string().nullish(),
  Description: z.string().nullish(),
  PriceReplacementText: z.string().nullish(),
  IsFromWebPortal: z.string().nullish(),
});

export const StoreSchemaDiamondAdjustments = z.object({
  CommodityKey: z.string(),
  Quantity: z.number(),
  Tags: z.string(),
  Metadata: z.string(),
});

const StoreListingBaseSchema = z.object({
  ReadableName: z.string().optional(),
  IsFirstPurchase: z.boolean().optional(),
  Name: z.string(),
  StartDate: z.string(),
  IapSku: z.string(),
  Debit: z.object({
    Adjustments: z.object(StoreSchemaDiamondAdjustments.shape).array(),
    CommodityInstanceIds: z.number().nullish(),
    PartialAdjustments: z.any().nullish(),
  }),
  Credit: z.object({
    Adjustments: z.object(StoreSchemaDiamondAdjustments.shape).array(),
    CommodityInstanceIds: z.number().nullish(),
    PartialAdjustments: z.any().nullish(),
  }),
  PurchaseLimitPerCustomer: z.number().nullish(),
  DisplayDetails: StoreSchemaDiamondDetails,
  SecondaryListing: z.string().nullish(),
  ExtraCredit: z.any(),
  Signature: z.string().nullish(),
  PurchaseLimitReset: z.number().nullish(),
  IsOriginalReplacement: z.boolean(),
  PurchaseLimitResetFixed: z.boolean(),
  Trigger: z.string().nullish(),
  UUID: z.number().nullish(),
  HasPerCustomerPurchaseLimit: z.boolean(),
  IsExternal: z.boolean(),
  PurchaseLimitId: z.string().nullish(), // link to the listing with the purchase limit
});

export const StoreListingSchema = z.object({
  ...StoreListingBaseSchema.shape,
  OriginalListing: StoreListingBaseSchema.nullish(),
  FallbackListing: StoreListingBaseSchema.nullish(),
});

export const StoreSchemaDiamondItems = z.object({
  Name: z.string(),
  StoreListing: StoreListingSchema,
});

export type TStoreDiamondItems = z.infer<typeof StoreSchemaDiamondItems>;
export type TStoreListing = z.infer<typeof StoreListingSchema>;

export const StoreSchemaDiamondFront = z.object({
  Name: z.string(),
  Listings: z.object(StoreSchemaDiamondItems.shape).array(),
  DisplayDetails: z.object({
    IsClickThroughEnabled: z.boolean(),
  }),
});

export type TStoreDiamondFront = z.infer<typeof StoreSchemaDiamondFront>;

export const StoreSchemaDiamond = z.object({
  StoreFront: StoreSchemaDiamondFront,
  StartDate: z.string().nullish(),
  EndDate: z.string().nullish(),
});

export type TStoreDiamond = z.infer<typeof StoreSchemaDiamond>;

const StorePurchaseCountsKeys = z.enum([
  "UserStoreListingPurchaseCounts",
  "AllTimeExternalPurchaseCounts",
  "LimitedPurchaseCountsWithResetTime",
]);

export const StoreSchema = ApiDefaultSchema.extend({
  Data: z.object({
    ListingState: z.any(),
    PurchaseCounts: z.record(StorePurchaseCountsKeys, z.any()),
    Store: z.object({
      StoreFronts: z.object({
        WebDiamonds: StoreSchemaDiamond,
        DiamondStore: StoreSchemaDiamond,
        WebSpecialOffers: StoreSchemaDiamond,
      }),
      Version: z.number().nullish(),
    }),
  }),
});

export type TStore = z.infer<typeof StoreSchema>;

export const LocalizedPricesItems = z.object({
  price: z.number(),
  sku: z.string(),
});

export const LocalizedPricesSchema = z.object({
  country: z.object({
    code: z.string(),
    currency: z.string(),
    symbol: z.string(),
    vatIncluded: z.boolean(),
  }),
  items: LocalizedPricesItems.array(),
});

export type TLocalizedPrices = z.infer<typeof LocalizedPricesSchema>;

export interface StoreItemProps {
  setAnalyticsOnTap: (subcategory: string, content: ScreenContent) => void;
}

export const STORE_NAV_ITEMS: TStoreNavAnchorNames[] = [
  "prizewheel",
  "piggybank",
  "diamonds",
  "bundles",
  "letters",
];

// TODO: rename variables to match others storefront items (make'em generic), as it is strongly attached to "diamonds"
